import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Palette, Sparkles, ImageIcon, Upload, X, Download } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { useBrainDefaults } from "../hooks/useBrainDefaults";
import { downloadMedia } from "../components/ManualDownloadPanel";
import { ShareToCommunityButton } from "../components/community/ShareToCommunityButton";
import { AssistantChat } from "../components/AssistantChat";
import { errMsg } from "../lib/errMsg";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RATIO_LABELS = {
  "1:1": "Square",
  "9:16": "Story / Reel",
  "4:5": "Portrait",
  "16:9": "Landscape",
  "3:4": "Classic",
  "21:9": "Cinematic",
};

const DesignerStudioPage = () => {
  const { token, refreshUser } = useAuth();
  const auth = { Authorization: `Bearer ${token}` };

  const [presets, setPresets] = useState([]);
  const [ratios, setRatios] = useState(["1:1", "9:16", "4:5", "16:9"]);
  const [prompt, setPrompt] = useState("");
  const [preset, setPreset] = useState(null);
  const [ratio, setRatio] = useState("1:1");
  const [refUrl, setRefUrl] = useState("");
  const [generating, setGenerating] = useState(false);
  const [results, setResults] = useState([]);
  // Photoreal mode injects camera/lens language + natural imperfections + the
  // "real moment, not a generated image" realism nudge. Default on for
  // product / lifestyle work; toggle off for logos / vector / illustration
  // where those cues would clash with the chosen aesthetic.
  const [photoreal, setPhotoreal] = useState(true);

  // XEA Brain — smart defaults for Designer realm. Applied once on first
  // mount only when the user hasn't manually touched the field yet.
  const { defaults: brainDefaults } = useBrainDefaults("designer");
  const [brainApplied, setBrainApplied] = useState(false);

  useEffect(() => {
    if (brainApplied) return;
    if (!brainDefaults || Object.keys(brainDefaults).length === 0) return;
    const applied = [];
    if (brainDefaults.style && !preset) {
      setPreset(brainDefaults.style); applied.push(`preset → ${brainDefaults.style}`);
    }
    if (brainDefaults.aspect_ratio && ratio === "1:1") {
      setRatio(brainDefaults.aspect_ratio); applied.push(`aspect → ${brainDefaults.aspect_ratio}`);
    }
    if (applied.length > 0) {
      toast.success(`🧠 XEA pre-filled your usual picks: ${applied.join(", ")}`, { duration: 4000 });
    }
    setBrainApplied(true);
  }, [brainDefaults]);

  /* eslint-disable react-hooks/exhaustive-deps, react-hooks/set-state-in-effect */
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/designer/presets`, { headers: auth });
        setPresets(r.data.presets || []);
        setRatios(r.data.aspect_ratios || ratios);
      } catch (e) {
        toast.error("Could not load presets.");
      }
    })();
    // Restore session — generations are saved server-side, so navigating
    // away (even mid-generation) never loses them.
    (async () => {
      try {
        const h = await axios.get(`${API}/designer/history`, { headers: auth });
        setResults((prev) => {
          const seen = new Set(prev.map((x) => x.id));
          return [...prev, ...(h.data.items || []).filter((x) => !seen.has(x.id))];
        });
      } catch (_e) { /* non-fatal */ }
    })();
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps, react-hooks/set-state-in-effect */

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error("Give me a prompt to riff on.");
      return;
    }
    setGenerating(true);
    try {
      const r = await axios.post(
        `${API}/designer/generate`,
        {
          prompt: prompt.trim(),
          preset,
          aspect_ratio: ratio,
          reference_image_url: refUrl.trim() || null,
          kind: "image",
          photoreal_mode: photoreal,
        },
        { headers: auth }
      );
      setResults((prev) => [r.data.item, ...prev]);
      toast.success(`Done · ${r.data.tokens_after} XEA left`);
      refreshUser?.();
    } catch (e) {
      const status = e?.response?.status;
      const detail = errMsg(e, "Generation failed.");
      if (status === 402) toast.error(`Out of tokens — ${detail}`);
      else toast.error(detail);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div
      className="max-w-7xl mx-auto px-4 py-8 space-y-8"
      data-testid="designer-studio-page"
    >
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-2xl bg-violet-500 flex items-center justify-center shadow-md">
          <Palette className="w-6 h-6 text-white" />
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-violet-500">
            Designer Studio
          </p>
          <h1 className="text-3xl md:text-4xl font-black text-ink-950 tracking-tight">
            Pure prompt. No store. No limits.
          </h1>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left — controls */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-ink-200 rounded-3xl p-6 space-y-4">
            {/* AI assistant — clarifying questions + emits 🖼️ IMAGE PROMPT marker */}
            <div className="rounded-2xl border border-violet-200 bg-gradient-to-br from-violet-50/40 to-white p-3">
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-violet-600 mb-2 flex items-center gap-1.5">
                <Sparkles className="w-3 h-3" /> Ask XEA for a design prompt
              </p>
              <AssistantChat
                variant="embed"
                contextHint={
                  refUrl
                    ? "I uploaded a reference. Suggest a recompose — change angle, setting, pose…"
                    : "Describe what you want to design — a logo, poster, product shot, etc."
                }
                usePromptLabel="Use as design prompt"
                onUsePrompt={(text) => {
                  // Same marker pattern as video but for IMAGES.
                  // Format: 🖼️ **IMAGE PROMPT** · Aspect: 1:1\n\n<body>
                  const m = text.match(
                    /🖼️\s*\*?\*?IMAGE PROMPT\*?\*?\s*·\s*Aspect:\s*([\d:]+)\s*\n+([\s\S]+?)(?:\n+✨|\n\n[A-Z]|$)/i,
                  );
                  if (m) {
                    const recRatio = m[1].trim();
                    const promptBody = m[2].trim();
                    if (ratios.includes(recRatio)) {
                      setRatio(recRatio);
                    }
                    setPrompt(promptBody.slice(0, 1500));
                    toast.success(
                      `Prompt applied · AI suggested aspect ${recRatio}`,
                    );
                  } else {
                    setPrompt(text.slice(0, 1500));
                    toast.success("Prompt copied into the editor");
                  }
                }}
              />
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink-500 mb-2 block">
                Prompt
              </label>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={3}
                maxLength={1500}
                placeholder="A retro-futuristic Athens skyline at sunrise, cinematic..."
                data-testid="designer-prompt"
                className="w-full p-3 rounded-xl border border-ink-200 focus:outline-none focus:border-violet-500 text-sm resize-none"
              />
              <p className="text-[10px] text-ink-500 mt-1 text-right">{prompt.length}/1500</p>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink-500 mb-2 block">
                Aesthetic preset
              </label>
              <div className="flex flex-wrap gap-2">
                {presets.map((p) => {
                  const active = preset === p.id;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setPreset(active ? null : p.id)}
                      data-testid={`designer-preset-${p.id}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-all ${
                        active
                          ? "bg-violet-500 text-white border-violet-500"
                          : "bg-white text-ink-700 border-ink-200 hover:border-violet-500"
                      }`}
                    >
                      {p.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink-500 mb-2 block">
                Aspect ratio
              </label>
              <div className="flex flex-wrap gap-2">
                {ratios.map((r) => {
                  const active = ratio === r;
                  return (
                    <button
                      key={r}
                      type="button"
                      onClick={() => setRatio(r)}
                      data-testid={`designer-ratio-${r}`}
                      className={`px-3 py-1.5 rounded-xl text-xs font-bold border transition-all ${
                        active
                          ? "bg-ink-950 text-white border-ink-950"
                          : "bg-white text-ink-700 border-ink-200 hover:border-ink-950"
                      }`}
                    >
                      {r}{" "}
                      <span className="font-normal opacity-70">
                        {RATIO_LABELS[r] || ""}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink-500 mb-2 block">
                Reference image URL (optional)
              </label>
              <div className="flex items-center gap-2">
                <Upload className="w-4 h-4 text-ink-500 shrink-0" />
                <input
                  value={refUrl}
                  onChange={(e) => setRefUrl(e.target.value)}
                  placeholder="https://… paste any reference for style transfer"
                  data-testid="designer-refurl"
                  className="flex-1 p-2 rounded-xl border border-ink-200 focus:outline-none focus:border-violet-500 text-sm"
                />
                {refUrl && (
                  <button
                    onClick={() => setRefUrl("")}
                    className="p-2 hover:bg-ink-100 rounded-full text-ink-500"
                    aria-label="Clear reference"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>

            {/* Photoreal vs Stylized — research-backed realism levers
                (camera/lens language + imperfection cues + "real moment"
                phrase) only help for photographic outputs. Logos /
                illustrations / vector designs work better without them. */}
            <div>
              <label className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink-500 mb-2 block">
                Aesthetic intent
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setPhotoreal(true)}
                  data-testid="designer-photoreal-on"
                  className={`p-2 rounded-xl border text-left transition ${
                    photoreal
                      ? "border-violet-500 bg-violet-50 ring-2 ring-violet-300"
                      : "border-ink-200 bg-white hover:border-ink-400"
                  }`}
                >
                  <div className="text-xs font-semibold">📸 Photoreal</div>
                  <div className="text-[10px] text-ink-500 mt-0.5">
                    Product shots, lifestyle, faces
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setPhotoreal(false)}
                  data-testid="designer-photoreal-off"
                  className={`p-2 rounded-xl border text-left transition ${
                    !photoreal
                      ? "border-violet-500 bg-violet-50 ring-2 ring-violet-300"
                      : "border-ink-200 bg-white hover:border-ink-400"
                  }`}
                >
                  <div className="text-xs font-semibold">🎨 Stylized</div>
                  <div className="text-[10px] text-ink-500 mt-0.5">
                    Logos, illustration, vector, 3D
                  </div>
                </button>
              </div>
            </div>

            <Button
              onClick={handleGenerate}
              disabled={generating || !prompt.trim()}
              data-testid="designer-generate"
              className="w-full bg-violet-500 hover:bg-violet-600 text-white"
            >
              {generating ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4" />
              )}
              Generate · 5 XEA
            </Button>
          </div>
        </div>

        {/* Right — results */}
        <div className="space-y-4">
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink-500">
            Your session
          </p>
          {results.length === 0 ? (
            <div className="rounded-3xl border-2 border-dashed border-ink-200 p-10 text-center bg-white">
              <ImageIcon className="w-10 h-10 mx-auto text-ink-300 mb-3" />
              <p className="text-sm text-ink-500">
                Your generations will show up here.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {results.map((r) => (
                <div
                  key={r.id}
                  className="bg-white rounded-2xl overflow-hidden border border-ink-200"
                  data-testid={`designer-result-${r.id}`}
                >
                  <img src={r.image_url} alt="" className="w-full object-cover" />
                  <div className="p-3 space-y-2">
                    <p className="text-xs text-ink-700 line-clamp-2">{r.prompt}</p>
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full border-ink-300 text-ink-800 hover:bg-ink-100"
                      data-testid={`designer-download-${r.id}`}
                      onClick={() => downloadMedia(r.image_url, `xea-design-${r.id.slice(0, 8)}`)}
                    >
                      <Download className="w-3.5 h-3.5 mr-1.5" /> Download
                    </Button>
                    <ShareToCommunityButton
                      sourceCollection="content"
                      sourceId={r.id}
                      size="sm"
                      variant="outline"
                      className="w-full border-violet-500/40 text-violet-600 hover:bg-violet-500/10"
                      testId={`designer-share-${r.id}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DesignerStudioPage;
